import type { MRT_ColumnDef, MRT_RowData } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import type { CreatedByUserObject } from '@visorpro/client'

import { useUserById } from '../../stores/user-store'
import { formatUserName } from '../../util/format'
import type { Stores } from '../../util/store'

export interface CreatedByCellProps {
    userId: string | null
    stores: Stores
}

export const CreatedByCell = observer<CreatedByCellProps>(
    ({ userId, stores }) => {
        const user = useUserById(stores.users, userId)

        if (user) {
            return <>{formatUserName(user) ?? user.id}</>
        }

        // Users are fetched separately from the rows, so hold the cell empty
        // rather than flashing a raw id at every row while that request runs.
        if (userId && !stores.users.isFetched) {
            return null
        }

        return (
            <span
                style={{
                    color: 'var(--comment-color)',
                    fontSize: '0.8rem',
                    fontStyle: 'italic',
                }}>
                {userId ? 'unknown user' : 'not recorded'}
            </span>
        )
    },
)

/**
 * The creator's name as it is rendered, for sorting and filtering. Falls back
 * to the raw id so a creator missing from the user list is still matchable.
 */
const getCreatedByName = (stores: Stores, userId: string | null): string => {
    if (!userId) {
        return ''
    }

    const user = stores.users.usersById[userId]
    if (!user) {
        return userId
    }

    return formatUserName(user) ?? user.id
}

/**
 * A "Created By" column resolving `created_by_user_id` against the user store.
 *
 * @example
 * ```tsx
 * const columns = useMemo<MRT_ColumnDef<APIProductManufacturer>[]>(
 *     () => [..., createdByColumnDef(stores)],
 *     [stores],
 * )
 * ```
 */
export const createdByColumnDef = <
    RowData extends MRT_RowData & CreatedByUserObject,
>(
    stores: Stores,
): MRT_ColumnDef<RowData> => ({
    id: 'created_by_user_id',
    accessorFn: (row) => row.created_by_user_id,
    header: 'Created By',
    size: 200,
    Cell: ({ row }) => (
        <CreatedByCell
            userId={row.original.created_by_user_id}
            stores={stores}
        />
    ),
    sortingFn: (rowA, rowB) =>
        getCreatedByName(stores, rowA.original.created_by_user_id).localeCompare(
            getCreatedByName(stores, rowB.original.created_by_user_id),
        ),
    filterFn: (row, _, filterValue: string) =>
        getCreatedByName(stores, row.original.created_by_user_id)
            .toLowerCase()
            .includes(filterValue.toLowerCase()),
})
